import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  FilePlus,
  Copy,
  Trash,
  PencilSimple,
  CalendarBlank,
  Funnel,
  UploadSimple,
  Spinner,
} from "@phosphor-icons/react";
import { format } from "date-fns";

const ProposalsPage = () => {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);
  const pdfInputRef = useRef(null);

  useEffect(() => {
    fetchProposals();
  }, [search, statusFilter]);

  const fetchProposals = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter) params.append("status", statusFilter);
      
      const res = await api.get(`/proposals?${params.toString()}`);
      setProposals(res.data);
    } catch (error) {
      toast.error("Failed to fetch proposals");
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const res = await api.post(`/proposals/${id}/duplicate`);
      toast.success("Proposal duplicated");
      navigate(`/proposals/${res.data.id}/edit`);
    } catch (error) {
      toast.error("Failed to duplicate proposal");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/proposals/${deleteId}`);
      toast.success("Proposal deleted");
      setDeleteId(null);
      fetchProposals();
    } catch (error) {
      toast.error("Failed to delete proposal");
    }
  };

  const handleImportPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/import-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success("PDF imported — opening in editor");
      // Navigate to new proposal with imported data in state
      navigate("/proposals/new?type=combined", { state: { importedData: res.data } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import PDF");
    } finally {
      setImporting(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "confirmed": return "status-confirmed";
      case "sent": return "status-sent";
      case "cancelled": return "status-cancelled";
      default: return "status-draft";
    }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="proposals-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold mb-2">Proposals</p>
          <h1 className="font-serif text-4xl text-navy">All Proposals</h1>
        </div>
        <div className="flex gap-2">
          <input type="file" ref={pdfInputRef} accept=".pdf" onChange={handleImportPdf} className="hidden" />
          <Button
            variant="outline"
            onClick={() => pdfInputRef.current?.click()}
            disabled={importing}
            className="gap-2 border-border text-navy"
            data-testid="import-pdf-btn"
          >
            {importing ? <Spinner size={16} className="animate-spin" /> : <UploadSimple size={16} />}
            {importing ? "Importing..." : "Import from PDF"}
          </Button>
          <Button
            onClick={() => navigate("/proposals/new")}
            className="bg-navy hover:bg-[#2a3a5e] text-white gap-2"
            data-testid="new-proposal-btn"
          >
            <FilePlus size={18} />
            New Proposal
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by client, trip title, or reference..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-border"
            data-testid="search-proposals"
          />
        </div>
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px] bg-white border-border">
            <Funnel size={16} className="mr-2 text-muted-foreground" />
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Draft">Draft</SelectItem>
            <SelectItem value="Sent">Sent</SelectItem>
            <SelectItem value="Confirmed">Confirmed</SelectItem>
            <SelectItem value="Cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Proposals Table */}
      <div className="bg-white border border-border">
        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin mx-auto"></div>
          </div>
        ) : proposals.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reference</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Client</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trip</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Value</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-border hover:bg-cream/50 cursor-pointer"
                  onClick={() => navigate(`/proposals/${p.id}/edit`)}
                  data-testid={`proposal-row-${p.id}`}
                >
                  <td className="p-4">
                    <span className="text-xs font-mono text-muted-foreground">{p.reference}</span>
                  </td>
                  <td className="p-4">
                    <span className="font-medium text-navy">{p.client_name}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-muted-foreground">{p.trip_title || "—"}</span>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 ${getStatusClass(p.status)}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="font-serif text-gold">{formatCurrency(p.total_value)}</span>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                      <CalendarBlank size={12} />
                      {p.created_at ? format(new Date(p.created_at), "MMM d, yyyy") : ""}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/proposals/${p.id}/edit`)}
                        className="h-8 w-8 p-0"
                      >
                        <PencilSimple size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(p.id)}
                        className="h-8 w-8 p-0"
                        data-testid={`duplicate-${p.id}`}
                      >
                        <Copy size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteId(p.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-${p.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-12 text-center">
            <FilePlus size={48} className="mx-auto text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground mb-4">No proposals found</p>
            <Button
              onClick={() => navigate("/proposals/new")}
              className="bg-gold hover:bg-gold/90 text-white"
            >
              Create your first proposal
            </Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Proposal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this proposal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProposalsPage;
